import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiClient } from '../../utils/api';

interface NewsFormData {
  title: string;
  content: string;
  publishDate: string;
  status: 'draft' | 'published';
  headerImage?: string;
}

const AdminNewsForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [formData, setFormData] = useState<NewsFormData>({
    title: '',
    content: '',
    publishDate: new Date().toISOString().slice(0, 16), // Format for datetime-local input
    status: 'draft'
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  // Load post data if editing
  useEffect(() => {
    if (isEditing && id) {
      fetchPost();
    }
  }, [isEditing, id]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/news/admin/all`);
      const post = response.data.posts.find((p: any) => p.id === id);
      
      if (post) {
        setFormData({
          title: post.title,
          content: post.content,
          publishDate: new Date(post.publishDate).toISOString().slice(0, 16),
          status: post.status,
          headerImage: post.headerImage
        });
        setImagePreview(post.headerImage || null);
      } else {
        setError('News post not found');
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to load news post');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const uploadFormData = new FormData();
      uploadFormData.append('headerImage', file);

      const response = await apiClient.post('/news/admin/upload-header', uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setFormData(prev => ({
        ...prev,
        headerImage: response.data.imageUrl
      }));
      setImagePreview(response.data.imageUrl);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({
      ...prev,
      headerImage: undefined
    }));
    setImagePreview(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);

      const submitData = {
        ...formData,
        publishDate: new Date(formData.publishDate).toISOString()
      };

      if (isEditing) {
        await apiClient.put(`/news/admin/${id}`, submitData);
      } else {
        await apiClient.post('/news/admin', submitData);
      }

      navigate('/admin/news');
    } catch (error: any) {
      setError(error.response?.data?.message || `Failed to ${isEditing ? 'update' : 'create'} news post`);
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditing) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-highlight"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>
          {isEditing ? 'Edit News Post' : 'Create News Post'}
        </h1>
        <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
          {isEditing ? 'Update your news article' : 'Create a new news article'}
        </p>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Title */}
        <div>
          <label htmlFor="title" className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
            Title *
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            required
            maxLength={200}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-light-text dark:text-dark-text focus:ring-2 focus:ring-highlight focus:border-highlight"
            placeholder="Enter news post title"
          />
        </div>

        {/* Header Image */}
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
            Header Image
          </label>
          
          {imagePreview ? (
            <div className="mb-4">
              <img 
                src={imagePreview} 
                alt="Header preview" 
                className="w-full max-w-md h-48 object-cover rounded-md"
              />
              <button
                type="button"
                onClick={handleRemoveImage}
                className="mt-2 text-red-600 hover:text-red-500 text-sm"
              >
                Remove image
              </button>
            </div>
          ) : (
            <div className="mb-4">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={uploading}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
              />
              <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                Maximum file size: 5MB. Supported formats: JPEG, PNG, WebP
              </p>
            </div>
          )}
          
          {uploading && (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-highlight mr-2"></div>
              <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Uploading...</span>
            </div>
          )}
        </div>

        {/* Content */}
        <div>
          <label htmlFor="content" className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
            Content *
          </label>
          <textarea
            id="content"
            name="content"
            value={formData.content}
            onChange={handleInputChange}
            required
            rows={12}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
            placeholder="Write your news post content here. You can use HTML for formatting."
          />
          <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
            HTML formatting is supported. Use tags like &lt;p&gt;, &lt;strong&gt;, &lt;em&gt;, &lt;ul&gt;, &lt;ol&gt;, &lt;h2&gt;, etc.
          </p>
        </div>

        {/* Publish Date */}
        <div>
          <label htmlFor="publishDate" className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
            Publish Date *
          </label>
          <input
            type="datetime-local"
            id="publishDate"
            name="publishDate"
            value={formData.publishDate}
            onChange={handleInputChange}
            required
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
          />
          <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
            Posts with future dates will not appear until the scheduled time
          </p>
        </div>

        {/* Status */}
        <div>
          <label htmlFor="status" className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
            Status *
          </label>
          <select
            id="status"
            name="status"
            value={formData.status}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
          >
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
          <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
            Only published posts will be visible to users
          </p>
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/admin/news')}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-highlight text-black rounded-md hover:bg-highlight/80 transition-colors disabled:opacity-50"
          >
            {loading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {isEditing ? 'Updating...' : 'Creating...'}
              </div>
            ) : (
              isEditing ? 'Update Post' : 'Create Post'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminNewsForm; 